import invariant from "invariant";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { openUrl } from "../../../../utils/extension";
import { ExcludeReason, useImportZoomMeetingMutation } from "../../../graphql";
import useCurrentUser from "../../../hooks/useCurrentUser";
import { getErrorContent } from "../importMeetingErrors";
import {
  IntegrationMessage,
  IntegrationMessageProps,
  VideoIntegration,
} from "../IntegrationMessage";
import { LoadingPlaceholder } from "./LoadingPlaceholder";
import { ImportMeetingParams } from "./utils";

type ImportZoomNativeMeetingProps = Pick<
  ImportMeetingParams,
  "zoomMeetingId" | "meetingUrl" | "inZoomApp" | "entryPoint" | "forceImport"
> & {
  onError?: (error: string) => void;
};

export const ImportZoomNativeMeeting: React.FC<
  ImportZoomNativeMeetingProps
> = ({
  zoomMeetingId,
  meetingUrl,
  inZoomApp,
  entryPoint,
  forceImport,
  onError,
}) => {
  invariant(zoomMeetingId, "Missing Zoom meeting ID");

  const currentUser = useCurrentUser();
  const navigate = useNavigate();
  const [message, setMessage] = useState<IntegrationMessageProps | null>(null);
  const [importRetryCount, setImportRetryCount] = useState(0);
  const openZoom = !inZoomApp;

  const tryOpenZoom = (): void => {
    if (openZoom) {
      openUrl(meetingUrl).catch(() => {
        // failed to open the meeting with the extension
      });
    }
  };

  const [importZoomMeeting] = useImportZoomMeetingMutation({
    variables: {
      meetingId: parseInt(zoomMeetingId, 10),
      entryPoint,
      forceImport,
    },
    onCompleted: (data) => {
      tryOpenZoom();

      const zoomNativeMeeting = data?.importZoomMeeting?.meeting;
      if (!zoomNativeMeeting) {
        setMessage(null);
        return;
      }

      const uiMessage: IntegrationMessageProps = {
        primaryMessage: "",
        secondaryMessage: "",
        isError: false,
        videoIntegration: VideoIntegration.ZOOM,
        videoMeetingUrl: meetingUrl,
        showLaunchButton: openZoom,
      };

      if (!inZoomApp && zoomNativeMeeting.zoomAppAutoOpenEnabled) {
        uiMessage.primaryMessage =
          "The Interview Assistant will open automatically in Zoom once you've joined the meeting.";
        uiMessage.secondaryMessage = "Feel free to close this window.";
        uiMessage.showLaunchButton = true;
        uiMessage.showImportButton = false;
      } else if (zoomNativeMeeting.exclude) {
        const { excludeReason, hostId } = zoomNativeMeeting;
        let secondary = `Someone in this meeting ${
          excludeReason === ExcludeReason.GlobalExclude
            ? "is on the recording exclusion list in BrightHire"
            : "opted out of BrightHire"
        }`;
        secondary += ", but recording can still happen manually within Zoom.";

        const isOptOut = [
          ExcludeReason.CandidateOptOut,
          ExcludeReason.InterviewerOptOut,
        ].includes(excludeReason as ExcludeReason);
        const isHost = hostId === currentUser.id;

        uiMessage.primaryMessage =
          "BrightHire is not scheduled to record the interview.";
        uiMessage.actionMessage = isOptOut
          ? "Recording can still happen manually in Zoom but will not be available in BrightHire."
          : isHost
          ? `To override that setting, hit 'Record' in Zoom.`
          : `To override that setting, the host can hit 'Record' in Zoom.`;
        uiMessage.secondaryMessage = secondary;
      } else if (!zoomNativeMeeting.importMeeting) {
        uiMessage.primaryMessage =
          "BrightHire is not scheduled to record the interview.";
        uiMessage.isError = true;
        uiMessage.showImportButton = true;
      } else if (zoomNativeMeeting.callId) {
        navigate(`/${zoomNativeMeeting.callId}`);
        uiMessage.primaryMessage = "Taking you to your meeting...";
      } else {
        uiMessage.primaryMessage = `We’re waiting for Zoom to tell us the recording has started.`;
        uiMessage.actionMessage = `If possible, hit 'Record' in Zoom.`;
        uiMessage.secondaryMessage = `Once Zoom sends us the meeting info, we’ll load your BrightHire experience.`;

        if (importRetryCount < 2) {
          setImportRetryCount((c) => c + 1);
          importZoomMeeting();
        }
      }

      setMessage(uiMessage);
    },
    onError: (error) => {
      tryOpenZoom();
      onError?.(error.message);

      const errorMessage = getErrorContent(error.message);
      setMessage({
        primaryMessage: errorMessage.primary,
        secondaryMessage: errorMessage.secondary,
        isError: true,
        videoIntegration: VideoIntegration.ZOOM,
        videoMeetingUrl: meetingUrl,
        showLaunchButton: openZoom,
      });
    },
  });

  useEffect(() => {
    importZoomMeeting();
  }, []);

  if (message) {
    return <IntegrationMessage {...message} inZoomApp={inZoomApp} />;
  }

  return <LoadingPlaceholder />;
};
